/**
 * useExercises.js
 * Custom hook for exercise tracking functionality
 * 
 * Extracted from App.js for maintainability (P1 decomposition)
 * NO behavior changes - extraction only
 * 
 * Features:
 * - Load today's exercises
 * - Log new exercises
 * - Log steps
 * - Delete exercises
 * - Track daily steps and calories burned
 */

import { useState, useCallback, useRef, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Custom hook for managing exercise state and operations
 * @param {string} userId - Current user ID
 * @returns {object} Exercise state and functions
 */
export const useExercises = (userId) => {
  // State
  const [todayExercises, setTodayExercises] = useState([]);
  const [dailySteps, setDailySteps] = useState(0);
  const [totalCaloriesBurned, setTotalCaloriesBurned] = useState(0);
  const [showExerciseLogger, setShowExerciseLogger] = useState(false);
  const [exerciseForm, setExerciseForm] = useState({
    activity: '',
    duration_minutes: '',
    intensity: 'Moderate'
  });
  const [isLoggingExercise, setIsLoggingExercise] = useState(false);

  // Ref for tracking mounted state (defensive cleanup)
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Load today's exercises for a user
   * @param {string} uid - User ID to load exercises for
   * @param {number} tzOffset - Timezone offset in hours from UTC (optional)
   */
  const loadTodayExercises = useCallback(async (uid, tzOffset) => {
    if (!uid) return;

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      // Calculate timezone offset if not provided
      const offset = tzOffset ?? Math.round(-new Date().getTimezoneOffset() / 60);
      
      const response = await fetch(
        `${BACKEND_URL}/api/exercises/today/${uid}?tz_offset=${offset}`,
        { signal: abortControllerRef.current.signal }
      );
      
      if (response.ok && isMountedRef.current) {
        const data = await response.json();
        setTodayExercises(data.exercises);
        setTotalCaloriesBurned(data.total_calories_burned);
        
        // Extract steps if present
        const stepsEntry = data.exercises.find(ex => ex.activity === 'Walking (Steps)');
        if (stepsEntry) {
          setDailySteps(stepsEntry.steps || 0);
        } else {
          // Reset steps if no steps entry for today
          setDailySteps(0);
        }
      }
    } catch (error) {
      // Don't log abort errors
      if (error.name !== 'AbortError') {
        console.error('Error loading today exercises:', error);
      }
    }
  }, []);

  /**
   * Log steps for the current user
   * @param {number} steps - Number of steps to log
   */
  const logSteps = useCallback(async (steps) => {
    if (!userId) return;

    try {
      // Calculate timezone offset
      const tzOffset = Math.round(-new Date().getTimezoneOffset() / 60);
      
      const response = await fetch(`${BACKEND_URL}/api/steps/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          steps: parseInt(steps),
          tz_offset: tzOffset
        })
      });

      if (response.ok && isMountedRef.current) {
        const data = await response.json();
        await loadTodayExercises(userId, tzOffset);
        alert(`✅ ${steps} steps logged! Calories burned: ${data.calories_burned} kcal`);
      }
    } catch (error) {
      console.error('Error logging steps:', error);
      alert('Failed to log steps');
    }
  }, [userId, loadTodayExercises]);

  /**
   * Log a new exercise
   * Uses exerciseForm state for activity, duration, and intensity
   */
  const logExercise = useCallback(async () => {
    if (!exerciseForm.activity || !exerciseForm.duration_minutes) {
      alert('Please select activity and enter duration');
      return;
    }

    if (!userId) return;

    try {
      setIsLoggingExercise(true);
      // Calculate timezone offset
      const tzOffset = Math.round(-new Date().getTimezoneOffset() / 60);
      
      const response = await fetch(`${BACKEND_URL}/api/exercises/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          activity: exerciseForm.activity,
          duration_minutes: parseInt(exerciseForm.duration_minutes),
          intensity: exerciseForm.intensity,
          tz_offset: tzOffset
        })
      });

      if (response.ok && isMountedRef.current) {
        const data = await response.json();
        await loadTodayExercises(userId, tzOffset);
        setShowExerciseLogger(false);
        setExerciseForm({ activity: '', duration_minutes: '', intensity: 'Moderate' });
        alert(`✅ Exercise logged!\n${data.explanation}\nCalories burned: ${data.calories_burned} kcal`);
      }
    } catch (error) {
      console.error('Error logging exercise:', error);
      alert('Failed to log exercise');
    } finally {
      if (isMountedRef.current) {
        setIsLoggingExercise(false);
      }
    }
  }, [userId, exerciseForm, loadTodayExercises]);

  /**
   * Delete an exercise by ID
   * @param {string} exerciseId - ID of exercise to delete
   */
  const deleteExercise = useCallback(async (exerciseId) => {
    if (!userId) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/exercises/${exerciseId}`, {
        method: 'DELETE'
      });

      if (response.ok && isMountedRef.current) {
        await loadTodayExercises(userId);
        alert('✅ Exercise deleted');
      }
    } catch (error) {
      console.error('Error deleting exercise:', error);
      alert('Failed to delete exercise');
    }
  }, [userId, loadTodayExercises]);

  /**
   * Update exercise form field
   * @param {string} field - Field name to update
   * @param {any} value - New value
   */
  const updateExerciseForm = useCallback((field, value) => {
    setExerciseForm(prev => ({ ...prev, [field]: value }));
  }, []);

  /**
   * Reset exercise form to defaults
   */
  const resetExerciseForm = useCallback(() => {
    setExerciseForm({ activity: '', duration_minutes: '', intensity: 'Moderate' });
  }, []);

  return {
    // State
    todayExercises,
    dailySteps,
    totalCaloriesBurned,
    showExerciseLogger,
    exerciseForm,
    isLoggingExercise,
    
    // Setters (for UI components that need direct control)
    setShowExerciseLogger,
    setExerciseForm,
    
    // Functions
    loadTodayExercises,
    logSteps,
    logExercise,
    deleteExercise,
    updateExerciseForm,
    resetExerciseForm
  };
};

export default useExercises;
